/**
 * Permissions Plugin
 *
 * Provides role checking and permission validation for the CRM application.
 *
 * ROLE HELPERS:
 *   $isAdmin()    - Returns true if user is admin or managing admin
 *   $isProvider() - Returns true if user is a provider
 *   $isClient()   - Returns true if user is a client/patient
 *
 * PERMISSION CHECK:
 *   $can(key, level) - Checks if user has the required permission level
 *
 *   @param {string} key   - Permission key (e.g., 'providers', 'address', 'payDetails')
 *   @param {string} level - Required level: 'view', 'edit', 'create', 'delete', 'all'
 *   @returns {boolean}
 *
 * USAGE PATTERNS:
 *
 *   // Admin-only check
 *   $isAdmin() && $can('providers', 'edit')
 *
 *   // Provider-only check
 *   $isProvider() && $can('address', 'view')
 *
 *   // Multi-role access (admin always sees, provider needs permission)
 *   $isAdmin() || ($isProvider() && $can('email', 'view'))
 *
 *   // Different permissions per role
 *   ($isAdmin() && $can('payrollReport', 'view')) || ($isProvider() && $can('payDetails', 'view'))
 *
 * V-CAN DIRECTIVE:
 *   <div v-can:edit.admin="'providers'">Admin only with providers.edit</div>
 *   <div v-can:view.provider="'address'">Provider only with address.view</div>
 *
 * KEY MAPPING:
 *   Keys matching backend names exactly (providers, clients, address, etc.) need no mapping.
 *   Only keys with different naming conventions are mapped (e.g., payDetails → pay_details).
 */

const PERMISSION_LEVELS = {
  noAccess: 0,
  view: 1,
  edit: 2,
  create: 3,
  delete: 4,
  all: 5,
};

const KEY_MAP = {
  // Admin
  adminUser: "admin_users",
  payrollReport: "payroll_report",
  billingReport: "billing_report",
  maxHoursReport: "max_hours_report",
  // Provider
  payDetails: "pay_details",
  birthDate: "birth_date",
  phones: "phone_number",
  emails: "email",
  sin: "SIN",
  HSTNumber: "HST_Number",
};

export default {
  install(app, { store }) {
    app.config.globalProperties.$isAdmin = function () {
      const user = store.state.auth.user;
      return !!(user?.isAdmin || user?.isManagingAdmin);
    };

    app.config.globalProperties.$isSuperAdmin = function () {
      const user = store.state.auth.user;
      return !!user?.isAdmin;
    };

    app.config.globalProperties.$isManagingAdmin = function () {
      const user = store.state.auth.user;
      return !!user?.isManagingAdmin;
    };

    app.config.globalProperties.$isProvider = function () {
      return !!store.state.auth.user?.isProvider;
    };

    app.config.globalProperties.$isClient = function () {
      return !!store.state.auth.user?.isPatient;
    };

    app.config.globalProperties.$can = function (keyOrParam, level) {
      const user = store.state.auth.user;
      const permissions = user?.permissions;
      if (!permissions) return false;

      let key, requiredLevel;
      if (typeof keyOrParam === "object") {
        key = keyOrParam.key;
        requiredLevel = keyOrParam.expectedPermission || "view";
      } else {
        key = keyOrParam;
        requiredLevel = level || "view";
      }

      if (key.includes(".")) {
        key = key.split(".").pop();
      }

      const permissionName = KEY_MAP[key] || key;

      if (!(permissionName in permissions)) {
        return false;
      }

      const userLevel = permissions[permissionName];
      const required = PERMISSION_LEVELS[requiredLevel] ?? 0;

      if (userLevel === 0) return false;
      if (required === 3 && userLevel === 4) return false;
      return userLevel >= required;
    };

    app.directive("can", {
      mounted(el, binding) {
        updateVisibility(el, binding, store);
      },

      updated(el, binding) {
        updateVisibility(el, binding, store);
      },
    });

    function updateVisibility(el, binding, store) {
      const level = binding.arg || "view";
      const key = binding.value;
      const { admin, provider, client } = binding.modifiers;
      const user = store.state.auth.user;

      if (admin && !(user?.isAdmin || user?.isManagingAdmin)) {
        el.style.display = "none";
        return;
      }
      if (provider && !user?.isProvider) {
        el.style.display = "none";
        return;
      }
      if (client && !user?.isPatient) {
        el.style.display = "none";
        return;
      }

      const permissions = user?.permissions;
      if (!permissions) {
        el.style.display = "none";
        return;
      }

      const permissionName = KEY_MAP[key] || key;
      if (!(permissionName in permissions)) {
        el.style.display = "none";
        return;
      }

      const userLevel = permissions[permissionName];
      const required = PERMISSION_LEVELS[level] ?? 0;

      if (userLevel === 0 || userLevel < required) {
        el.style.display = "none";
      } else {
        el.style.display = "";
      }
    }
  },
};
